// src/auth/authActions.js
// Import axios for making HTTP requests
import axios from 'axios';

/**
 * Asynchronous action creator for logging in a user.
 * 
 * @param {string} username - The username entered by the user.
 * @param {string} password - The password entered by the user.
 * @returns An async function that takes dispatch as an argument.
 */
export const loginUser = (username, password) => async dispatch => {
    try {
        const backendUrl = process.env.REACT_APP_BACKEND_API_URL;
        console.log("Sending login request"); // Debugging log
        // Attempt to POST the username and password to the login API endpoint
        const response = await axios.post(`${backendUrl}/token/`, { username, password });
        console.log("Login response", response); // Check response

        // Extract the token from the response data
        const token = response.data.token;

        // Assuming you have a header setup for subsequent requests
        axios.defaults.headers.common['Authorization'] = `Token ${token}`;

        // Fetch user data
        const userInfoResponse = await axios.get(`${backendUrl}/users/users/`);
        console.log("User info response", userInfoResponse);

        let userInfo;
        if (userInfoResponse.data.length === 1) {
            // Directly use the single returned user object
            userInfo = userInfoResponse.data[0];
        } else {
            // Fallback to finding the user by username if necessary (e.g., for staff members accessing multiple users)
            userInfo = userInfoResponse.data.find(user => user.username === username);
        }

        // Dispatch login success with token and user data
        dispatch({ type: 'LOGIN_SUCCESS', payload: { token, userInfo } });

        // Store the token and user data in localStorage or another suitable storage
        localStorage.setItem('token', token);
        localStorage.setItem('userInfo', JSON.stringify(userInfo));

        // Log a success message to the console
        console.log("Successful login");
    } catch (error) {
        // If the error response exists, log the error data from the server
        if (error.response) {
            console.error('Error data:', error.response || error);
        } else {
            // If the error does not have a response (e.g., network error), log the error message
            console.error('Error:', error.message);
        }
    }
};

// New action type for resetting auth error
export const RESET_AUTH_ERROR = 'RESET_AUTH_ERROR';

// Action creator for resetting auth error
export const resetAuthError = () => ({
    type: RESET_AUTH_ERROR,
});

// Logout action to the backend API
export const logoutUser = () => async dispatch => {
    try {
        await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/users/logout/`, {}, {
            headers: {
                'Authorization': `Token ${localStorage.getItem('token')}`
            }
        });

        // Dispatch the logout action
        dispatch({ type: 'LOGOUT_SUCCESS' });

        // Clear the user token from localStorage
        localStorage.removeItem('token');
    } catch (error) {
        console.error("Logout failed", error);
        // Handle error (e.g., dispatch a logout failure action, notify the user)
    }
};
